import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TableSortLabel,
  TablePagination,
  Grid,
  Autocomplete,
  TextField,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Avatar,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { usePermiteAcesso } from "../../Components/Funcionais/Utils";
import { DelAPI } from "../../Components/Backend/DelAPI";
import { GetAPI } from "../../Components/Backend/GetAPI";
import AddIcon from "@mui/icons-material/Add";
import CreateIcon from "@mui/icons-material/Create";
import DeleteIcon from "@mui/icons-material/Delete";
import APIAxios from "../../Api/APIAxios";
import SessaoAuth from "../../Hooks/SessaoAuth";
import PageTitle from "../../Components/Layout/PageTitle";

export default function UsersList() {
  //***************************************   AVISOS E LOADING ************************************************* */
  // CONTROLE DE AVISOS E ALERTAS
  //Funcão para fechar avisos e alertas
  usePermiteAcesso(2); //Controle de Acessos
  const [avisoErro, setAvisoErro] = useState("");
  const [avisoSucesso, setAvisoSucesso] = useState("");
  const [loading, setLoading] = useState(false);
  //************************************************************************************************** */

  const { sessao, setSessao } = SessaoAuth();

  const setRowsPerPage = (newRowsPerPage) => {
    setSessao((prev) => ({ ...prev, rowsPerPage: newRowsPerPage }));
  };

  const apiAxios = APIAxios();
  const navigate = useNavigate(); // Hook para navegação

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null); // Armazena o computador selecionado para o menu
  const [sortColumn, setSortColumn] = useState(null); // Coluna ordenada
  const [sortDirection, setSortDirection] = useState("asc"); // Direção da ordenação
  const [cmpSelecao, setCmpSelecao] = useState(20); // default: descrição
  const [valorSelecionado, setValorSelecionado] = useState(""); // NOVO
  const [page, setPage] = useState(0);
  const [openExcluir, setOpenExcluir] = useState(false);
  const [avatarUrls, setAvatarUrls] = useState({});

  useEffect(() => {
    // Função para buscar dados da API
    const fetchUsers = async () => {
      try {
        setAvisoErro("");
        setLoading(true);
        const response = await apiAxios.get("/usuarios");
        setLoading(false);

        setUsers(response.data.data);
      } catch (error) {
        setAvisoErro("Não foi possível carregar os gestores.");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  //Função para seleção de filtro
  const getAutocompleteOptions = () => {
    let options = [];

    switch (cmpSelecao) {
      case 10:
        options = users.map((u) => u.id);
        break;
      case 20:
        options = users.map((u) => u.nome);
        break;
      case 30:
        options = users.map((u) => u.cargo);
        break;
      case 40:
        options = users.map((u) => u.email);
        break;
      case 50:
        options = users.map((u) => u.nomPerfil);
        break;
      case 60:
        options = users.map((u) => (u.status === "A" ? "Ativo" : "Inativo"));

        break;

      default:
        return [];
    }

    options = [...new Set(options)]; // Remove duplicatas

    // Ordena de forma alfabética ou numérica
    return options.sort((a, b) => {
      if (typeof a === "string") return a.localeCompare(b); // ordenação alfabética
      return a - b; // ordenação numérica
    });
  };

  // NOVO: Filtra conforme o valor selecionado
  const usuariosFiltrados = valorSelecionado
    ? users.filter((user) => {
      const valor = valorSelecionado.toLocaleLowerCase();
      switch (cmpSelecao) {
        case 10:
          return user.id.toString().includes(valorSelecionado);
        case 20:
          return user.nome.toLowerCase().includes(valor);
        case 30:
          return user.cargo.toLowerCase().includes(valor);
        case 40:
          return user.email.toLowerCase().includes(valor);
        case 50:
          return user.nomPerfil.toLowerCase().includes(valor);
        case 60:
          return (user.status === "A" ? "ativo" : "inativo").includes(valor);
        default:
          return true;
      }
    })
    : users;

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleIncluirUsuario = () => {
    navigate("/usuarios/registro");
  };

  // Função para manipular a ordenação
  const handleSort = (column) => {
    const isAsc = sortColumn === column && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortColumn(column);
  };

  // Função para ordenar os dados com base na coluna selecionada
  const sortedUsers = [...usuariosFiltrados].sort((a, b) => {
    if (!sortColumn) return 0;
    const valueA = a[sortColumn] || "";
    const valueB = b[sortColumn] || "";

    if (typeof valueA === "string") {
      return sortDirection === "asc"
        ? valueA.localeCompare(valueB)
        : valueB.localeCompare(valueA);
    } else {
      return sortDirection === "asc" ? valueA - valueB : valueB - valueA;
    }
  });

  const paginatedUsers = sortedUsers.slice(
    page * (sessao.rowsPerPage || 10),
    page * (sessao.rowsPerPage || 10) + (sessao.rowsPerPage || 10)
  );

  // Funções para abrir e fechar o menu de opções
  const handleEdit = (event, user) => {
    setSelectedUser(user);
    navigate(`/usuario/${user.id}`);
  };

  // Função para abrir o diálogo de exclusão
  const handleClickOpenExcluir = (e, reg) => {
    setSelectedUser(reg);
    setOpenExcluir(true);
  };

  const handleClickCloseExcluir = () => {
    setOpenExcluir(false);
  };

  const excluirUsuario = async () => {
    try {
      setOpenExcluir(false);

      setLoading(true);
      const response = await DelAPI(apiAxios, `usuario/${selectedUser.id}`);
      setLoading(false);

      if (response.success) {
        // Remove o item da lista sem recarregar a página
        setUsers((prev) => prev.filter((reg) => reg.id !== selectedUser.id));
        setAvisoSucesso("Exclusão efetuada com sucesso!");
      } else {
        setAvisoErro(
          response.errorMessage || "Não foi possível excluir o fabricante."
        );
      }
    } catch (error) {
      setAvisoErro("Não foi possível excluir o gestor.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(0);
  }, [usuariosFiltrados]);

  const carregarAvatar = async (id) => {

    const response = await GetAPI(apiAxios, '/usuario/avatar/' + id,
      {
        responseType: "blob",
      });

    // Cria uma URL temporária para o blob recebido
    const blobUrl = URL.createObjectURL(response.data);

    return blobUrl;

  };

  useEffect(() => {
    let isMounted = true;

    const idsDaPagina = (paginatedUsers || [])
      .map((c) => c.id)
      .filter((id) => id != null);

    const idsParaBuscar = idsDaPagina.filter((id) => !avatarUrls[id]);

    if (idsParaBuscar.length === 0) return;

    (async () => {
      const results = await Promise.all(
        idsParaBuscar.map(async (id) => {
          const url = await carregarAvatar(id);
          return { id, url };
        })
      );

      if (!isMounted) return;

      setAvatarUrls((prev) => {
        const next = { ...prev };
        for (const { id, url } of results) {
          if (url) next[id] = url;
        }
        return next;
      });
    })();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginatedUsers]);

  return (
    <Box>
      {/***************************************** AVISOS **********************************/}
      <PageTitle
        caption="Lista de Gestores"
        subtitle="Listagem de Gestores"
        setAvisoErro={setAvisoErro}
        avisoErro={avisoErro}
        avisoSucesso={avisoSucesso}
        setAvisoSucesso={setAvisoSucesso}
        loading={loading}
        accentStyle
      />
      {/***************************************** AVISOS **********************************/}

      {/* Diálogo para Excluir Usuário */}
      <Dialog
        open={openExcluir}
        onClose={handleClickCloseExcluir}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Deseja Excluir este Gestor?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <br />
            <strong>ATENÇÃO! MUITO IMPORTANTE!</strong>
            <br />
            <br />
            Após a exclusão, todos os registros deste gestor serão excluídos
            DEFINITIVAMENTE.
            <br />
            Confirma a EXCLUSÃO?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClickCloseExcluir}>Cancelar</Button>
          <Button onClick={excluirUsuario} autoFocus>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
      {/* Diálogo para Excluir Usuario */}

      <Box>
        <Grid container spacing={2} alignItems={"center"}>
          {/*Usar meia tela em caso de monitor grande e tela total em monitores pequenos*/}
          <Grid item xs={12} md={3}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <FormControl fullWidth>
                <InputLabel>Filtrar por:</InputLabel>
                <Select
                  labelId="cmpSelecao"
                  id="cmpSelecao"
                  label="Filtrar por"
                  value={cmpSelecao}
                  onChange={(e) => setCmpSelecao(e.target.value)}
                >
                  <MenuItem value={10}>Código</MenuItem>
                  <MenuItem value={20}>Nome</MenuItem>
                  <MenuItem value={30}>Cargo</MenuItem>
                  <MenuItem value={40}>Email</MenuItem>
                  <MenuItem value={50}>Perfil</MenuItem>
                  <MenuItem value={60}>Situação</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              id="cmpDescricao"
              freeSolo
              options={getAutocompleteOptions()}
              value={valorSelecionado}
              onInputChange={(event, newValue) => setValorSelecionado(newValue)} // NOVO
              renderInput={(params) => (
                <TextField {...params} label="Localizar" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}></Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ backgroundColor: "#1a3865" }}
              onClick={handleIncluirUsuario}
            >
              Incluir Gestor
            </Button>
          </Grid>
        </Grid>
        <Box>
          <Paper sx={{ mt: 2 }} elevation={4}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">
                      <TableSortLabel
                        active={sortColumn === "id"}
                        direction={sortColumn === "id" ? sortDirection : "asc"}
                        onClick={() => handleSort("id")}
                      >
                        Código
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === "nome"}
                        direction={
                          sortColumn === "nome" ? sortDirection : "asc"
                        }
                        onClick={() => handleSort("nome")}
                      >
                        Nome
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === "cargo"}
                        direction={
                          sortColumn === "cargo" ? sortDirection : "asc"
                        }
                        onClick={() => handleSort("cargo")}
                      >
                        Cargo
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === "email"}
                        direction={
                          sortColumn === "email" ? sortDirection : "asc"
                        }
                        onClick={() => handleSort("email")}
                      >
                        E-mail
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === "nomPerfil"}
                        direction={
                          sortColumn === "nomPerfil" ? sortDirection : "asc"
                        }
                        onClick={() => handleSort("nomPerfil")}
                      >
                        Perfil
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">
                      <TableSortLabel
                        active={sortColumn === "status"}
                        direction={
                          sortColumn === "status" ? sortDirection : "asc"
                        }
                        onClick={() => handleSort("status")}
                      >
                        Situação
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">
                      {/*ícones editar e excluir*/}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell align="center">{user.id}</TableCell>

                      {/* Célula alterada para incluir o Avatar lado a lado com o nome */}
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar
                            src={avatarUrls[user.id] || undefined}
                            sx={{ width: 32, height: 32 }}
                          />
                          {user.nome}
                        </Box>
                      </TableCell>

                      <TableCell>{user.cargo}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.nomPerfil}</TableCell>
                      <TableCell align="center">
                        {user.status === "A" ? "Ativo" : "Inativo"}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          aria-label="options"
                          onClick={(event) => handleEdit(event, user)}
                        >
                          <CreateIcon style={{ color: "#1a3865" }} />
                        </IconButton>
                        <IconButton
                          aria-label="deletar"
                          onClick={(event) =>
                            handleClickOpenExcluir(event, user)
                          }
                        >
                          <DeleteIcon style={{ color: "#f4793c" }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={users.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={sessao.rowsPerPage || 10}
              labelRowsPerPage="Linhas por página"
              rowsPerPageOptions={[5, 10, 25]}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                "& .MuiTablePagination-toolbar": {
                  alignItems: "center",
                },
                "& p": {
                  marginBottom: 0,
                },
              }}
            />
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
